import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { createUserFolderInS3 } from '../utils/s3Utils';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlus, FiEdit2, FiTrash2, FiUser, FiFolder, FiKey, FiSave, FiX, FiCheck, FiAlertCircle, FiLoader, FiInfo } = FiIcons;

function UserManagement() {
  const { users, createUser, updateUser, deleteUser } = useAuth();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    password: '',
    s3Folder: ''
  });
  const [creatingUser, setCreatingUser] = useState(false);
  const [folderCreationStatus, setFolderCreationStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreatingUser(true);
    setFolderCreationStatus(null);
    
    try {
      let newUser;
      if (editingUser) {
        updateUser(editingUser.id, formData);
        setEditingUser(null);
      } else {
        // Create user first to get the ID
        newUser = createUser(formData);
        console.log('👤 User created locally:', newUser);
        
        // Create S3 folder for the new user
        try {
          console.log('📁 Attempting to create S3 folder...');
          const folderResult = await createUserFolderInS3(
            newUser.id.toString(),
            newUser.username
          );
          
          console.log('✅ S3 folder creation result:', folderResult);
          
          setFolderCreationStatus({
            success: true,
            message: `Successfully created S3 folder for ${newUser.username}`
          });
        } catch (folderError) {
          console.error('❌ Failed to create S3 folder:', folderError);
          
          setFolderCreationStatus({
            success: false,
            message: `User created but failed to create S3 folder: ${folderError.message}`
          });
        }
      }
      
      setFormData({
        name: '',
        username: '',
        password: '',
        s3Folder: ''
      });
      setShowCreateForm(false);
    } catch (error) {
      console.error('❌ User creation error:', error);
      setFolderCreationStatus({
        success: false,
        message: `Failed to create user: ${error.message}`
      });
    } finally {
      setCreatingUser(false);
      // Clear folder status message after 10 seconds (increased for debugging)
      if (folderCreationStatus) {
        setTimeout(() => {
          setFolderCreationStatus(null);
        }, 10000);
      }
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      username: user.username,
      password: user.password,
      s3Folder: user.s3Folder
    });
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingUser(null);
    setFormData({
      name: '',
      username: '',
      password: '',
      s3Folder: ''
    });
    setFolderCreationStatus(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowCreateForm(true)}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span>Add User</span>
        </motion.button>
      </div>

      {/* Folder Creation Status Message */}
      <AnimatePresence>
        {folderCreationStatus && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`rounded-lg p-4 flex items-center space-x-3 ${
              folderCreationStatus.success 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}
          >
            <SafeIcon 
              icon={folderCreationStatus.success ? FiCheck : FiAlertCircle} 
              className={`w-5 h-5 ${
                folderCreationStatus.success ? 'text-green-600' : 'text-red-600'
              }`} 
            />
            <div className="flex-1">
              <p className={`text-sm ${
                folderCreationStatus.success ? 'text-green-700' : 'text-red-700'
              }`}>
                {folderCreationStatus.message}
              </p>
              {!folderCreationStatus.success && (
                <p className="text-xs text-red-600 mt-1">
                  Check the browser console for detailed error information.
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {(showCreateForm || editingUser) && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-gray-50 rounded-lg p-6 border border-gray-200"
          >
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {editingUser ? 'Edit User' : 'Create New User'}
            </h3>
            
            {editingUser && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700">
                  <SafeIcon icon={FiInfo} className="w-4 h-4 inline mr-2" />
                  Note: S3 folder is only created during initial user creation.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  disabled={creatingUser}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Username
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  disabled={creatingUser}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  disabled={creatingUser}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  S3 Folder
                </label>
                <input
                  type="text"
                  value={formData.s3Folder}
                  onChange={(e) => setFormData(prev => ({ ...prev, s3Folder: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., client-name-uploads"
                  required
                  disabled={creatingUser}
                />
              </div>
              <div className="md:col-span-2 flex items-center space-x-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="submit"
                  disabled={creatingUser}
                  className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creatingUser ? (
                    <>
                      <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                      <span>{editingUser ? 'Updating...' : 'Creating...'}</span>
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiSave} className="w-4 h-4" />
                      <span>{editingUser ? 'Update' : 'Create'}</span>
                    </>
                  )}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="button"
                  onClick={handleCancel}
                  disabled={creatingUser}
                  className="flex items-center space-x-2 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
                >
                  <SafeIcon icon={FiX} className="w-4 h-4" />
                  <span>Cancel</span>
                </motion.button>
              </div>
            </form>

            {!editingUser && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-700">
                  <SafeIcon icon={FiFolder} className="w-3 h-3 inline mr-1" />
                  A new S3 folder will be automatically created with the user's ID when the user is created.
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Client Users</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {users.map((user) => (
            <motion.div
              key={user.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="px-6 py-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiUser} className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">{user.name}</h4>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span className="flex items-center space-x-1">
                        <SafeIcon icon={FiKey} className="w-3 h-3" />
                        <span>{user.username}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <SafeIcon icon={FiFolder} className="w-3 h-3" />
                        <span>{user.s3Folder}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <SafeIcon icon={FiUser} className="w-3 h-3" />
                        <span>ID: {user.id}</span>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => handleEdit(user)}
                    className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => deleteUser(user.id)}
                    className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default UserManagement;